import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { isBrowser } from 'react-device-detect';
import { selectDeeplink, selectUserUuid } from '../signup/signupSlice';
import { setMetrics } from '../../analytics/eCommerce';
import { selectLandingType, selectPlan } from '../plans/plansSlice';
import UIButton from '../../UI/UIButton';
import { MediumTitle, fadeInAnimation, wh, screenPadding } from '../../mixins';
import { sendAnalyticsEvents } from '../../analytics/common';
import { EVENTS } from '../../analytics/events';
import {
  selectBraintreeNonce,
  selectID,
  selectPurchaseEventId,
  selectStripeNonce,
  selectUpsellData,
} from '../checkout/checkoutSlice';
import SuccessLogo from '../../UI/SuccessLogo';
import { selectEventsData } from '../events/eventsSlice';

const APP_ONE_LINK = 'https://gzl.onelink.me/UpQ4';

const StyledContainer = styled.div`
  ${wh('100%')};
  ${isBrowser && fadeInAnimation};
  ${screenPadding};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media screen and (min-width: 768px) {
    padding: 24px 6px 32px;
    height: calc(100vh - 116px);
    min-height: unset;
  }
`;

const Title = styled.h1`
  ${MediumTitle};
  margin: 32px 0;
`;

const spinning = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Loader = styled.div`
  width: 18px;
  height: 18px;
  background-image: url(${'../assets/spinner-image.svg'});
  background-repeat: no-repeat;
  background-position: center center;
  background-size: 100%;
  margin: auto;
  animation: ${spinning} 1.5s linear infinite;
`;

const SuccessScreen = () => {
  const { t } = useTranslation();
  const plan = useSelector(selectPlan);
  const subscriptionId = useSelector(selectID);
  const purchaseEventId = useSelector(selectPurchaseEventId);
  const userUuid = useSelector(selectUserUuid);
  const landingType = useSelector(selectLandingType);
  const deeplinkValue = useSelector(selectDeeplink);
  const eventsData = useSelector(selectEventsData);
  const upsellData = useSelector(selectUpsellData);
  const stripeNonce = useSelector(selectStripeNonce);
  const braintreeNonce = useSelector(selectBraintreeNonce);
  const eventId = `${stripeNonce || braintreeNonce}_${userUuid}`;
  const [deeplink, setDeeplink] = useState('');

  useEffect(
    () => sendAnalyticsEvents(EVENTS.successScreenShown, eventsData),
    [eventsData]
  );

  useEffect(() => {
    if (plan && subscriptionId) {
      const gclid = eventsData?.gclid || '';
      setMetrics(
        plan,
        subscriptionId,
        null,
        upsellData?.success,
        landingType,
        purchaseEventId,
        gclid
      );
    }
  }, [
    plan,
    subscriptionId,
    purchaseEventId,
    upsellData,
    landingType,
    eventId,
    eventsData,
  ]);

  const onStartClick = () => {
    if (!deeplink) return;
    window.location.href = deeplink;
  };

  useEffect(() => {
    const timeout = setTimeout(() => setDeeplink(APP_ONE_LINK), 7000);
    if (!deeplinkValue) return;
    clearTimeout(timeout);
    setDeeplink(deeplinkValue);
  }, [deeplinkValue]);

  return (
    <StyledContainer>
      <SuccessLogo />
      <Title>{t('your_plans_awaits')}</Title>
      <UIButton
        onClick={onStartClick}
        style={{ cursor: !deeplink ? 'not-allowed' : 'pointer' }}
      >
        {!deeplink ? <Loader /> : t('start_tracking')}
      </UIButton>
      <input type={'hidden'} id={'end-of-onboarding'} />
    </StyledContainer>
  );
};

export default SuccessScreen;
